// src/services/pdfExportService.js
//
// Rapport PDF d'optimisation TURPE — design pro moderne.
// Stack : jsPDF + jspdf-autotable (déjà installés).
//
// Structure du rapport :
//   1. Cover page (titre, économie clé, infos client)
//   2. Synthèse du choix final retenu (KPI)
//   3. Comparatif TURPE 6 vs TURPE 7 (composantes détaillées)
//   4. Optimisation des puissances souscrites (justification + tableau)
//   5. Courbe de charge avec PS actuelle / optimisée superposées
//   6. Recommandation version d'utilisation (CU vs LU)
//
// Toutes les couleurs sont alignées sur la palette du dashboard (Tailwind).

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Chart,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  DoughnutController,
  ArcElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Title,
  Filler,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

// Enregistrement unique des contrôleurs Chart.js utilisés
Chart.register(
  BarController, BarElement,
  LineController, LineElement, PointElement,
  DoughnutController, ArcElement,
  CategoryScale, LinearScale, TimeScale,
  Tooltip, Legend, Title, Filler,
  annotationPlugin,
);

// ─── Palette ──────────────────────────────────────────────────────────────
// Slate (gris bleutés modernes) > gray purs. Emerald enrichi + accents.
// Alias gray* conservés pour les graphes Chart.js et le code existant.
const COLORS = {
  primary: '#059669',        // emerald-600
  primaryDark: '#065f46',    // emerald-800 (plus profond, plus premium)
  primaryDeep: '#064e3b',    // emerald-900 (cover bandeau)
  primaryLight: '#d1fae5',   // emerald-100
  primarySoft: '#ecfdf5',    // emerald-50 (tints très claires)
  secondary: '#1e3a8a',      // blue-900
  secondaryLight: '#dbeafe', // blue-100
  accent: '#d97706',         // amber-600
  red: '#b91c1c',            // red-700
  redLight: '#fee2e2',
  // Slate (gris bleutés) — modernes, premium
  ink: '#0f172a',            // slate-900
  body: '#334155',           // slate-700
  muted: '#64748b',          // slate-500
  line: '#e2e8f0',           // slate-200
  lineSoft: '#f1f5f9',       // slate-100
  surface: '#f8fafc',        // slate-50
  // Alias rétrocompatibles (Chart.js + existant)
  gray900: '#0f172a',
  gray700: '#334155',
  gray500: '#64748b',
  gray300: '#e2e8f0',
  gray100: '#f1f5f9',
  gray50: '#f8fafc',
  white: '#ffffff',
};

// A4 portrait — marge légèrement plus généreuse pour respirer
const PAGE = { width: 210, height: 297, margin: 17 };

// Doc ID pseudo-unique (réutilisé en footer)
const buildDocId = () => {
  const d = new Date();
  const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const rand = Math.floor(Math.random() * 0xffff).toString(16).toUpperCase().padStart(4, '0');
  return `EG360-${stamp}-${rand}`;
};

// ─── Helpers ──────────────────────────────────────────────────────────────
// Helvetica jsPDF ne contient pas tous les glyphes Unicode (U+202F narrow nbsp,
// U+00A0 nbsp, U+2009 thin space, U+2212 minus) → rendus comme `/` ou `"`.
// On les remplace par leurs équivalents ASCII pour un rendu propre.
const asciiSafe = (s) => String(s)
  .replace(/[     ]/g, ' ')  // espaces unicode → espace normal
  .replace(/−/g, '-')                            // minus typographique → trait d'union
  .replace(/[–—]/g, '-')                    // en-dash/em-dash → trait d'union
  .replace(/→/g, '>').replace(/←/g, '<').replace(/[•·]/g, '-').replace(/…/g, '...');                         // ellipsis → 3 points

const fmtEUR = (v, digits = 0) => {
  if (v == null || isNaN(v)) return '- EUR';
  return asciiSafe(new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(Number(v)));
};
const fmtNum = (v) => v == null || isNaN(v) ? '-' : asciiSafe(Number(v).toLocaleString('fr-FR'));
const fmtDate = (d) => d ? asciiSafe(new Date(d).toLocaleDateString('fr-FR')) : '-';
const fmtPct = (v, digits = 1) => v == null || isNaN(v) ? '-' : `${Number(v) >= 0 ? '' : '-'}${Math.abs(Number(v)).toFixed(digits)} %`;

class TurpePDFService {
  // ─── Primitives de dessin ───────────────────────────────────────────────
  filledRect(x, y, w, h, color) {
    this.doc.setFillColor(color);
    this.doc.rect(x, y, w, h, 'F');
  }
  borderRect(x, y, w, h, color, lineWidth = 0.3) {
    this.doc.setDrawColor(color);
    this.doc.setLineWidth(lineWidth);
    this.doc.rect(x, y, w, h, 'S');
  }
  text(str, x, y, opts = {}) {
    const {
      size = 10, color = COLORS.ink, bold = false, align = 'left',
      maxWidth, italic = false, tracking = 0,
    } = opts;
    this.doc.setFontSize(size);
    this.doc.setTextColor(color);
    const style = bold ? (italic ? 'bolditalic' : 'bold') : (italic ? 'italic' : 'normal');
    this.doc.setFont('helvetica', style);
    if (tracking) this.doc.setCharSpace(tracking);
    if (maxWidth) this.doc.text(str, x, y, { align, maxWidth });
    else this.doc.text(str, x, y, { align });
    if (tracking) this.doc.setCharSpace(0);
  }

  /**
   * Eyebrow label : petit label uppercase tracé au-dessus d'un titre,
   * style éditorial. Donne du rythme et un look cabinet de conseil.
   */
  eyebrow(label, x, y, color = COLORS.primary) {
    this.text(label.toUpperCase(), x, y, { size: 7, color, bold: true, tracking: 0.8 });
  }

  /**
   * En-tête de section : eyebrow optionnel + titre H2 + filet fin.
   * Hiérarchie marquée (h2 = 14pt) pour un rythme éditorial pro.
   */
  sectionHeader(label, eyebrow = null) {
    const x = PAGE.margin;
    if (eyebrow) {
      this.eyebrow(eyebrow, x, this.yPos, COLORS.primary);
      this.yPos += 6;  // ⬅ espace entre eyebrow vert et titre principal (était 4)
    }
    this.filledRect(x, this.yPos - 4, 1.2, 7, COLORS.primary);
    this.text(label, x + 5, this.yPos + 1, { size: 14, color: COLORS.ink, bold: true });
    this.yPos += 6;  // ⬅ espace entre titre et filet (était 5)
    // Filet fin sous le titre
    this.doc.setDrawColor(COLORS.line);
    this.doc.setLineWidth(0.2);
    this.doc.line(x, this.yPos, PAGE.width - PAGE.margin, this.yPos);
    this.yPos += 7;  // ⬅ espace entre filet et contenu (était 6)
  }

  /** Paragraphe d'intro sous un section header. */
  intro(str) {
    this.text(str, PAGE.margin, this.yPos, {
      size: 9.5, color: COLORS.body, maxWidth: PAGE.width - 2 * PAGE.margin,
    });
    this.yPos += this._textLineHeight(str, PAGE.width - 2 * PAGE.margin, 9.5) + 2;
  }

  /**
   * Logo monogramme EG : cercle + accent décoratif + initiales.
   * Pas d'asset externe, full vectoriel jsPDF.
   */
  drawLogoMark(cx, cy, r, fg = COLORS.white, bg = COLORS.primary) {
    // Cercle principal
    this.doc.setFillColor(bg);
    this.doc.circle(cx, cy, r, 'F');
    // Accent : petit cercle en haut-droite
    this.doc.setFillColor(fg);
    this.doc.circle(cx + r * 0.55, cy - r * 0.55, r * 0.22, 'F');
    // Monogramme EG
    this.text('EG', cx, cy + r * 0.35, {
      size: r * 2.2, color: fg, bold: true, align: 'center',
    });
  }

  hr(color = COLORS.gray300) {
    this.doc.setDrawColor(color);
    this.doc.setLineWidth(0.2);
    this.doc.line(PAGE.margin, this.yPos, PAGE.width - PAGE.margin, this.yPos);
    this.yPos += 4;
  }

  ensureSpace(needed) {
    if (this.yPos + needed > PAGE.height - 20) {
      this.doc.addPage();
      this.yPos = PAGE.margin;
    }
  }

  // ─── Rendu Chart.js → image PNG ─────────────────────────────────────────
  /**
   * Rend un graphique Chart.js offscreen et retourne le dataURL PNG.
   * Resolution : SCALE × widthMm × 5.9 px/mm → environ 150 DPI.
   * Cleanup automatique : chart.destroy() + canvas.remove().
   */
  _renderChart(config, widthMm, heightMm) {
    const PX_PER_MM = 5.9;
    const SCALE = 2; // Retina-like sharpness
    const pxW = Math.round(widthMm * PX_PER_MM * SCALE);
    const pxH = Math.round(heightMm * PX_PER_MM * SCALE);

    const canvas = document.createElement('canvas');
    canvas.width = pxW;
    canvas.height = pxH;
    canvas.style.position = 'fixed';
    canvas.style.left = '-99999px';
    canvas.style.top = '-99999px';
    canvas.style.width = `${widthMm * PX_PER_MM}px`;
    canvas.style.height = `${heightMm * PX_PER_MM}px`;
    document.body.appendChild(canvas);

    // animation:false → premier draw synchrone (Chart.js 4)
    const cfg = {
      ...config,
      options: {
        responsive: false,
        maintainAspectRatio: false,
        animation: false,
        devicePixelRatio: 1,
        ...(config.options || {}),
      },
    };

    let dataUrl;
    let chart;
    try {
      chart = new Chart(canvas, cfg);
      chart.draw();
      dataUrl = canvas.toDataURL('image/png');
    } finally {
      if (chart) chart.destroy();
      canvas.remove();
    }
    return dataUrl;
  }

  /** Ajoute un graphique Chart.js au PDF à la position courante (yPos). */
  _embedChart(config, widthMm, heightMm) {
    try {
      const dataUrl = this._renderChart(config, widthMm, heightMm);
      this.doc.addImage(dataUrl, 'PNG', PAGE.margin, this.yPos, widthMm, heightMm, undefined, 'FAST');
      this.yPos += heightMm + 4;
      return true;
    } catch (err) {
      console.warn('[pdfExportService] Chart render failed:', err);
      return false;
    }
  }

  // ─── COVER PAGE ─────────────────────────────────────────────────────────
  buildCover(ctx) {
    const { pdlInfo, year, totalSaving, choiceLabel } = ctx;

    // Bandeau premium (emerald-900) avec formes décoratives géométriques.
    // Hauteur 95mm → respire et donne de la prestance.
    const bandH = 95;
    this.filledRect(0, 0, PAGE.width, bandH, COLORS.primaryDeep);

    // Cercles décoratifs débordants à droite (jeu de transparences simulé
    // par superposition de teintes emerald de plus en plus claires)
    this.doc.setFillColor(COLORS.primaryDark);
    this.doc.circle(PAGE.width + 15, 22, 55, 'F');
    this.doc.setFillColor(COLORS.primary);
    this.doc.circle(PAGE.width + 8, 32, 32, 'F');
    // Filet accent fin sous le bandeau (signature design)
    this.filledRect(0, bandH, PAGE.width, 0.8, COLORS.primary);

    // — Wordmark (haut-gauche) + Logo officiel (haut-droite) —
    this.text('ENERGYGROUP 360', PAGE.margin, 17, {
      size: 12, color: COLORS.white, bold: true, tracking: 0.6,
    });
    this.text('Conseil & optimisation énergétique', PAGE.margin, 23, {
      size: 8.5, color: COLORS.primaryLight, tracking: 0.3,
    });

    // Logo PNG officiel posé en haut-droite. Le logo source est 1109×188px
    // (ratio ~5.9:1). On respecte l'aspect ratio : 60mm de large → ~10mm de haut.
    if (ctx.logoDataUrl) {
      try {
        const logoW = 60;
        const logoH = 10.2;  // 60 / 5.9 ≈ 10.2 (respect aspect ratio)
        const cardPad = 2;
        const cardW = logoW + 2 * cardPad;
        const cardH = logoH + 2 * cardPad;
        const cardX = PAGE.width - PAGE.margin - cardW;
        const cardY = 13;
        this.doc.setFillColor(COLORS.white);
        this.doc.roundedRect(cardX, cardY, cardW, cardH, 1.5, 1.5, 'F');
        this.doc.addImage(ctx.logoDataUrl, 'PNG', cardX + cardPad, cardY + cardPad, logoW, logoH, undefined, 'FAST');
      } catch {
        this.drawLogoMark(PAGE.width - PAGE.margin - 8, 18, 7, COLORS.white, COLORS.primaryDeep);
      }
    } else {
      this.drawLogoMark(PAGE.width - PAGE.margin - 8, 18, 7, COLORS.white, COLORS.primaryDeep);
    }

    // — Titre principal (eyebrow + 2 lignes H1) —
    this.text('RAPPORT', PAGE.margin, 50, {
      size: 11, color: COLORS.primaryLight, bold: true, tracking: 2,
    });
    this.text("Optimisation tarifaire", PAGE.margin, 64, {
      size: 24, color: COLORS.white, bold: true,
    });
    this.text("TURPE & Puissances souscrites", PAGE.margin, 75, {
      size: 24, color: COLORS.white, bold: true,
    });

    // — Méta (exercice + référence) en pied de bandeau —
    this.text(`EXERCICE ${year || new Date().getFullYear()}`, PAGE.margin, 88, {
      size: 8, color: COLORS.primaryLight, bold: true, tracking: 1,
    });
    // Réf alignée right depuis le bord moins la marge, sans tracking pour ne pas
    // que jsPDF mal-calcule la largeur du texte et l'aligne en dehors de la page.
    this.text(`REF. ${this.docIdValue}`, PAGE.width - PAGE.margin, 88, {
      size: 8, color: COLORS.primaryLight, bold: true, align: 'right',
    });

    // ─── Bloc économie centrale (callout premium) ────────────────────
    // Fond emerald très clair + barre latérale signature = lit comme un
    // KPI majeur sans avoir besoin de bordure pleine.
    const blockY = 115;
    const blockH = 64;
    this.filledRect(PAGE.margin, blockY, PAGE.width - 2 * PAGE.margin, blockH, COLORS.primarySoft);
    this.filledRect(PAGE.margin, blockY, 3, blockH, COLORS.primary);

    this.eyebrow('Économie annuelle identifiée', PAGE.margin + 10, blockY + 11, COLORS.primaryDark);
    this.text(fmtEUR(totalSaving), PAGE.margin + 10, blockY + 36, {
      size: 38, color: COLORS.primaryDeep, bold: true,
    });
    this.text('Gain net annuel sur le poste acheminement (TURPE)', PAGE.margin + 10, blockY + 45, {
      size: 9.5, color: COLORS.muted,
    });
    if (choiceLabel) {
      // Filet séparateur interne fin
      this.doc.setDrawColor(COLORS.line);
      this.doc.setLineWidth(0.2);
      this.doc.line(PAGE.margin + 10, blockY + 51, PAGE.width - PAGE.margin - 10, blockY + 51);
      this.text(choiceLabel, PAGE.margin + 10, blockY + 58, {
        size: 9, color: COLORS.body,
      });
    }

    // ─── Infos client ────────────────────────────────────────────────
    // Nom de site en gros + filet sous-titre + table 2 colonnes minimaliste
    const clientY = 200;
    this.eyebrow('Point de livraison', PAGE.margin, clientY, COLORS.muted);
    this.text(pdlInfo.site_name || 'Site analysé', PAGE.margin, clientY + 9, {
      size: 16, color: COLORS.ink, bold: true,
    });
    this.doc.setDrawColor(COLORS.line);
    this.doc.setLineWidth(0.2);
    this.doc.line(PAGE.margin, clientY + 13, PAGE.width - PAGE.margin, clientY + 13);

    const infoRows = [
      ['Numéro PDL', pdlInfo.pdl_number || '—'],
      ['Adresse', this._formatAddress(pdlInfo.address)],
      ['Contrat actuel', ctx.optionTarifaire || '—'],
      ['Puissances souscrites', ctx.psCurrentLabel || '—'],
      ['Période analysée', ctx.periodLabel || '—'],
    ];
    autoTable(this.doc, {
      startY: clientY + 17,
      body: infoRows,
      theme: 'plain',
      styles: {
        fontSize: 9.5,
        cellPadding: { top: 2, bottom: 2, left: 0, right: 0 },
        textColor: COLORS.ink,
        lineWidth: 0,
      },
      columnStyles: {
        0: { cellWidth: 55, fontStyle: 'bold', textColor: COLORS.muted, fontSize: 8 },
        1: { textColor: COLORS.ink },
      },
      margin: { left: PAGE.margin, right: PAGE.margin },
    });

    // ─── Footer cover (filet + 3 zones) ──────────────────────────────
    this.doc.setDrawColor(COLORS.line);
    this.doc.setLineWidth(0.2);
    this.doc.line(PAGE.margin, PAGE.height - 22, PAGE.width - PAGE.margin, PAGE.height - 22);

    this.text('Document confidentiel', PAGE.margin, PAGE.height - 16, {
      size: 7, color: COLORS.muted, bold: true, tracking: 0.6,
    });
    this.text(`Établi le ${new Date().toLocaleDateString('fr-FR')} - Propriété : EnergyGroup`,
      PAGE.margin, PAGE.height - 12, { size: 8, color: COLORS.body });
  }

  // ─── PAGE 2 — SYNTHÈSE ──────────────────────────────────────────────────
  buildSynthesis(ctx) {
    this.doc.addPage();
    this.yPos = PAGE.margin;
    this.sectionHeader('Synthèse du choix retenu', 'Vue d\'ensemble');

    // ─── Bandeau choix final (callout premium) ────────────────────────
    const calloutH = 22;
    this.filledRect(PAGE.margin, this.yPos, PAGE.width - 2 * PAGE.margin, calloutH, COLORS.primarySoft);
    this.filledRect(PAGE.margin, this.yPos, 3, calloutH, COLORS.primary);
    this.eyebrow('Choix final validé', PAGE.margin + 8, this.yPos + 7, COLORS.primaryDark);
    this.text(ctx.choiceLabel || 'Aucun choix final encore validé',
      PAGE.margin + 8, this.yPos + 16, { size: 11, color: COLORS.ink, bold: true });
    this.yPos += calloutH + 10;

    // ─── 4 KPI cards en grille 2×2 (fiche métrique premium) ──────────
    // Refonte : icône geometric en pastille colorée + label uppercase
    // tracked + valeur XL + sub italique. Ombre simulée par rect décalé.
    const cardW = (PAGE.width - 2 * PAGE.margin - 6) / 2;
    const cardH = 32;
    const kpis = [
      {
        label: 'Coût TURPE actuel', value: fmtEUR(ctx.coutActuel),
        sub: 'TTC sur 12 mois', color: COLORS.secondary, icon: 'square',
      },
      {
        label: 'Coût TURPE optimisé', value: fmtEUR(ctx.coutOptimise),
        sub: ctx.versionChange && ctx.vouSaving > 0 ? 'après optimisation PS + version' : 'après optimisation PS',
        color: COLORS.primary, icon: 'circle',
      },
      {
        label: 'Économie annuelle', value: fmtEUR(ctx.totalSaving),
        sub: `${fmtEUR(ctx.totalSaving * 3)} sur 3 ans`, color: COLORS.primary, icon: 'arrow',
      },
      {
        label: 'Dépassements détectés', value: fmtNum(ctx.nbDepassements),
        sub: 'avant optimisation',
        color: ctx.nbDepassements > 0 ? COLORS.red : COLORS.primary, icon: 'triangle',
      },
    ];
    kpis.forEach((kpi, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = PAGE.margin + col * (cardW + 6);
      const y = this.yPos + row * (cardH + 6);

      // Ombre simulée + fond blanc + bordure très douce
      this.filledRect(x + 0.3, y + 0.3, cardW, cardH, COLORS.lineSoft);
      this.filledRect(x, y, cardW, cardH, COLORS.white);
      this.borderRect(x, y, cardW, cardH, COLORS.line, 0.2);

      // Pastille icône (cercle plein de couleur primaire + forme blanche)
      const iconCx = x + 8, iconCy = y + 9;
      this.doc.setFillColor(kpi.color);
      this.doc.circle(iconCx, iconCy, 3.2, 'F');
      this.doc.setFillColor(COLORS.white);
      if (kpi.icon === 'circle') this.doc.circle(iconCx, iconCy, 1, 'F');
      else if (kpi.icon === 'square') this.doc.rect(iconCx - 1, iconCy - 1, 2, 2, 'F');
      else if (kpi.icon === 'arrow') {
        this.doc.triangle(iconCx - 1.2, iconCy + 1.2, iconCx + 1.2, iconCy + 1.2, iconCx + 1.2, iconCy - 1.2, 'F');
      } else if (kpi.icon === 'triangle') {
        this.doc.triangle(iconCx, iconCy - 1.4, iconCx - 1.4, iconCy + 1.2, iconCx + 1.4, iconCy + 1.2, 'F');
      }

      // Label uppercase tracked
      this.text(kpi.label.toUpperCase(), x + 15, y + 8, {
        size: 7, color: COLORS.muted, bold: true, tracking: 0.4,
      });
      // Valeur XL en ink (pas en couleur primaire — la couleur reste sur l'icône)
      this.text(kpi.value, x + 5, y + 21, {
        size: 18, color: COLORS.ink, bold: true,
      });
      // Sub discret
      this.text(kpi.sub, x + 5, y + 28, {
        size: 8, color: COLORS.muted,
      });
    });
    this.yPos += 2 * (cardH + 6) + 6;

    // ─── Synthèse narrative ──────────────────────────────────────────
    this.sectionHeader('Synthèse exécutive', 'Lecture pour décideur');
    const ecartTurpe = ctx.turpe6Ttc - ctx.turpe7Ttc;
    const senseTurpe = ecartTurpe >= 0 ? 'allègera' : 'alourdira';
    const narrative = [
      `Le passage au TURPE 7 (en vigueur au 01/08/2025) ${senseTurpe} la facture d'acheminement de ${fmtEUR(Math.abs(ecartTurpe))} par an, à configuration contractuelle inchangée.`,
      `En réajustant les puissances souscrites au profil réel de consommation, le client capte ${fmtEUR(ctx.psSaving)} d'économie supplémentaire chaque année — sans rénovation ni changement de fournisseur.`,
      ctx.vouStatut === 'non_mesure' || ctx.vouStatut === 'surcout'
        ? `Version d'utilisation : économie non retenue. ${ctx.vouCause || ''}`
        : ctx.versionChange && ctx.vouSaving > 0
        ? `Bascule de la version d'utilisation ${ctx.versionActuelle} → ${ctx.versionChosen} : ${fmtEUR(ctx.vouSaving)} d'économie supplémentaire chaque année${ctx.vouPuissanceCalcul ? ` (calculée à la puissance proposée ${['hph', 'hch', 'hpb', 'hcb'].map((c) => ctx.vouPuissanceCalcul[c]).filter((v) => v != null).join('/')} kVA)` : ' (puissance de calcul non enregistrée)'} — changement de grille tarifaire ENEDIS, sans impact sur la consommation.`
        : `La version d'utilisation ${ctx.versionActuelle || 'CU'} en place est ${ctx.versionChange ? `sous-optimale : la bascule vers ${ctx.versionChosen} est recommandée.` : 'la bonne grille pour ce profil. Aucun arbitrage à mener.'}`,
      `Action prioritaire : valider la nouvelle grille de puissances${ctx.versionChange ? ` et la version ${ctx.versionChosen}` : ''}. Pic mesuré ${fmtNum(ctx.puissanceMaxKw)} kW pour une PS souscrite de ${fmtNum(ctx.psActualMax)} kVA — la marge confirme la faisabilité.`,
    ];
    narrative.forEach(line => {
      // Puce custom : petit carré emerald (mieux qu'un bullet point)
      this.filledRect(PAGE.margin, this.yPos - 2.2, 1.5, 1.5, COLORS.primary);
      const lh = this._textLineHeight(line, PAGE.width - 2 * PAGE.margin - 5, 9.5);
      this.text(line, PAGE.margin + 5, this.yPos, {
        size: 9.5, color: COLORS.body, maxWidth: PAGE.width - 2 * PAGE.margin - 5,
      });
      this.yPos += lh + 1.5;
    });
  }

  // ─── PAGE 3 — COMPARATIF TURPE 6 vs 7 ───────────────────────────────────
  buildComparatif(ctx) {
    this.doc.addPage();
    this.yPos = PAGE.margin;
    this.sectionHeader('Comparatif TURPE 6 vs TURPE 7', 'Évolution réglementaire');

    this.intro(
      `Le TURPE 7 entre en vigueur le 01/08/2025. Il redistribue les composantes du tarif d'acheminement fixé par la CRE. Comparaison ci-dessous à puissances souscrites identiques, pour isoler le seul effet de la nouvelle grille.`,
    );

    const { t6, t7 } = ctx;
    const t6HT = t6.cout_total_htva || 0;
    const t7HT = t7.cout_total_htva || 0;
    const rows = [
      ['Composante Gestion (CG)', fmtEUR(t6.composante_gestion, 2), fmtEUR(t7.composante_gestion, 2)],
      ['Composante Comptage (CC)', fmtEUR(t6.composante_comptage, 2), fmtEUR(t7.composante_comptage, 2)],
      ['Soutirage Fixe (CSF)', fmtEUR(t6.composante_soutirage_fixe, 2), fmtEUR(t7.composante_soutirage_fixe, 2)],
      ['Soutirage Variable (CSV)', fmtEUR(t6.composante_soutirage_variable, 2), fmtEUR(t7.composante_soutirage_variable, 2)],
      ['Dépassements (CMDPS)', fmtEUR(t6.composante_depassement, 2), fmtEUR(t7.composante_depassement, 2)],
      ['CACNC', '—', fmtEUR(t7.composante_cacnc, 2)],
    ];

    autoTable(this.doc, {
      startY: this.yPos,
      head: [['Composante', 'TURPE 6 (actuel)', 'TURPE 7 (au 01/08/2025)']],
      body: rows,
      foot: [
        // jspdf-autotable : columnStyles.halign ne s'applique PAS au foot. Il
        // faut poser halign manuellement sur chaque cellule numérique pour
        // que les totaux soient alignés à droite comme le body.
        ['Total HT',    { content: fmtEUR(t6HT, 2),       styles: { fontStyle: 'bold', halign: 'right' } }, { content: fmtEUR(t7HT, 2),       styles: { fontStyle: 'bold', halign: 'right' } }],
        ['TVA (20 %)', { content: fmtEUR(t6HT * 0.2, 2), styles: { halign: 'right' } },                     { content: fmtEUR(t7HT * 0.2, 2), styles: { halign: 'right' } }],
        ['Total TTC',  { content: fmtEUR(t6HT * 1.2, 2), styles: { fontStyle: 'bold', textColor: COLORS.primary, halign: 'right' } }, { content: fmtEUR(t7HT * 1.2, 2), styles: { fontStyle: 'bold', textColor: COLORS.primary, halign: 'right' } }],
      ],
      // Refonte table : theme striped (zebra subtil) + bordures retirées
      // (cleaner que 'grid') + padding aéré.
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.ink, textColor: COLORS.white,
        fontSize: 8, fontStyle: 'bold', halign: 'left',
        cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
        lineWidth: 0,
      },
      bodyStyles: {
        fontSize: 9, textColor: COLORS.ink,
        cellPadding: { top: 2.8, bottom: 2.8, left: 4, right: 4 },
        lineWidth: 0,
      },
      alternateRowStyles: { fillColor: COLORS.surface },
      footStyles: {
        fillColor: COLORS.lineSoft, textColor: COLORS.ink,
        fontSize: 9, lineWidth: 0,
        cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { halign: 'right' },
        2: { halign: 'right' },
      },
      margin: { left: PAGE.margin, right: PAGE.margin },
    });
    this.yPos = this.doc.lastAutoTable.finalY + 8;

    // Bar chart Chart.js — comparaison par composante TURPE 6 vs TURPE 7
    const labels = ['CG', 'CC', 'CSF', 'CSV', 'CMDPS'];
    const t6Vals = [
      Number(t6.composante_gestion) || 0,
      Number(t6.composante_comptage) || 0,
      Number(t6.composante_soutirage_fixe) || 0,
      Number(t6.composante_soutirage_variable) || 0,
      Number(t6.composante_depassement) || 0,
    ];
    const t7Vals = [
      Number(t7.composante_gestion) || 0,
      Number(t7.composante_comptage) || 0,
      Number(t7.composante_soutirage_fixe) || 0,
      Number(t7.composante_soutirage_variable) || 0,
      Number(t7.composante_depassement) || 0,
    ];

    const barConfig = {
      type: 'bar',
      data: {
        labels,
        datasets: [
          { label: 'TURPE 6 (actuel)', data: t6Vals, backgroundColor: COLORS.secondary, borderRadius: 4, maxBarThickness: 28 },
          { label: 'TURPE 7 (01/08/2025)', data: t7Vals, backgroundColor: COLORS.primary, borderRadius: 4, maxBarThickness: 28 },
        ],
      },
      options: {
        plugins: {
          legend: { position: 'top', labels: { font: { size: 22 }, color: COLORS.gray700, boxWidth: 18 } },
          title: { display: true, text: 'Composantes TURPE — comparaison HT (€/an)', font: { size: 24, weight: 'bold' }, color: COLORS.gray900, padding: { top: 4, bottom: 12 } },
          tooltip: { enabled: false },
        },
        scales: {
          x: { ticks: { font: { size: 22 }, color: COLORS.gray700 }, grid: { display: false } },
          y: {
            beginAtZero: true,
            ticks: {
              font: { size: 18 }, color: COLORS.gray500,
              callback: (v) => `${(v / 1000).toFixed(1)}k €`,
            },
            grid: { color: COLORS.gray100 },
          },
        },
      },
    };
    const chartW = PAGE.width - 2 * PAGE.margin;
    const chartH = 75;
    this._embedChart(barConfig, chartW, chartH);

    // ─── Callout Impact total HT (delta TURPE 6 → 7) ─────────────────
    this.ensureSpace(20);
    const deltaHT = t7HT - t6HT;
    const isUp = deltaHT > 0;
    const deltaTxt = isUp
      ? `+${fmtEUR(deltaHT, 0)} avec TURPE 7`
      : `${fmtEUR(deltaHT, 0)} avec TURPE 7`;
    const calloutH = 16;
    this.filledRect(PAGE.margin, this.yPos, PAGE.width - 2 * PAGE.margin, calloutH,
      isUp ? COLORS.redLight : COLORS.primarySoft);
    this.filledRect(PAGE.margin, this.yPos, 3, calloutH,
      isUp ? COLORS.red : COLORS.primary);
    this.eyebrow('Impact total HT', PAGE.margin + 8, this.yPos + 6,
      isUp ? COLORS.red : COLORS.primaryDark);
    this.text(deltaTxt, PAGE.width - PAGE.margin - 5, this.yPos + 11, {
      size: 12, bold: true,
      color: isUp ? COLORS.red : COLORS.primaryDeep, align: 'right',
    });
    this.yPos += calloutH + 4;
  }

  // ─── PAGE 4 — OPTIMISATION DES PS ───────────────────────────────────────
  buildPSOptim(ctx) {
    this.doc.addPage();
    this.yPos = PAGE.margin;
    this.sectionHeader('Optimisation des puissances souscrites', 'Levier principal');

    this.intro(
      `Les puissances souscrites (PS) déterminent l'abonnement payé sur chacun des quatre cadrans horaires. Trop hautes, elles surfacturent ; trop basses, elles déclenchent des pénalités de dépassement (CMDPS). Ci-dessous, le réglage retenu (${ctx.choiceLabelShort || 'optimisation moteur'}) confronté à la situation actuelle et aux pics réellement mesurés.`,
    );

    const rows = ['HPH', 'HCH', 'HPB', 'HCB'].map(p => {
      const pk = p.toLowerCase();
      const current = ctx.psCurrent?.[pk] || 0;
      const proposed = ctx.psProposed?.[pk] || 0;
      const max = ctx.psMaxAtteint?.[pk] || 0;
      const reduction = current > 0 ? ((current - proposed) / current * 100) : 0;
      return [
        p,
        `${current} kVA`,
        `${max.toFixed(1)} kW`,
        `${proposed} kVA`,
        { content: `${reduction >= 0 ? '-' : '+'}${Math.abs(reduction).toFixed(1)} %`, styles: { textColor: reduction >= 0 ? COLORS.primary : COLORS.red, fontStyle: 'bold' } },
      ];
    });

    autoTable(this.doc, {
      startY: this.yPos,
      head: [['Cadran', 'PS actuelle', 'Pic mesuré', 'PS retenue', 'Variation']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.ink, textColor: COLORS.white,
        fontSize: 8, fontStyle: 'bold', halign: 'center',
        cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
        lineWidth: 0,
      },
      bodyStyles: {
        fontSize: 10, halign: 'center', textColor: COLORS.ink,
        cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
        lineWidth: 0,
      },
      alternateRowStyles: { fillColor: COLORS.surface },
      columnStyles: { 0: { fontStyle: 'bold', halign: 'left', textColor: COLORS.primaryDark } },
      margin: { left: PAGE.margin, right: PAGE.margin },
    });
    this.yPos = this.doc.lastAutoTable.finalY + 10;

    // ─── Économie générée — callout premium ───────────────────────────
    const calloutH = 24;
    this.filledRect(PAGE.margin, this.yPos, PAGE.width - 2 * PAGE.margin, calloutH, COLORS.primarySoft);
    this.filledRect(PAGE.margin, this.yPos, 3, calloutH, COLORS.primary);
    this.eyebrow('Économie générée par l\'optimisation PS', PAGE.margin + 8, this.yPos + 8, COLORS.primaryDark);
    this.text(`${fmtEUR(ctx.psSaving)}/an`, PAGE.margin + 8, this.yPos + 19, {
      size: 16, color: COLORS.primaryDeep, bold: true,
    });
    if (ctx.psSafetyMargin != null) {
      this.text(`Marge de sécurité : +${ctx.psSafetyMargin} %`, PAGE.width - PAGE.margin - 5, this.yPos + 18, {
        size: 8, color: COLORS.muted, align: 'right',
      });
    }
    this.yPos += calloutH + 10;

    // ─── Justification ────────────────────────────────────────────────
    this.sectionHeader('Justification de la recommandation', 'Notre analyse');
    const depMsg = ctx.nbDepassements === 0
      ? `aucun dépassement n'apparaît sur l'historique`
      : `${ctx.nbDepassements} dépassement(s) ont été détectés et sont absorbés par la nouvelle grille`;
    const notes = [
      `Mécanique de l'économie : la composante Soutirage Fixe (CSF) du TURPE est facturée au kVA souscrit par cadran. Chaque kVA évité réduit directement l'abonnement annuel, sans toucher au tarif variable ni à la fourniture.`,
      `Données mesurées sur ${ctx.year} : pic instantané ${fmtNum(ctx.puissanceMaxKw)} kW contre PS maximale souscrite de ${fmtNum(ctx.psActualMax)} kVA. Le taux d'utilisation moyen ressort à ${ctx.tauxUtilisation ? ctx.tauxUtilisation.toFixed(1) + ' %' : 'non calculable'} — un écart qui confirme la surcapacité contractuelle.`,
      `Sécurisation : la PS retenue conserve une marge au-dessus du pic mesuré pour absorber les démarrages moteur, montées en charge ou aléas climatiques. Objectif : économiser sans exposer le site à la pénalité CMDPS (facturée 12 fois le tarif normal sur la fraction dépassée). Sur la base de l'historique, ${depMsg}.`,
    ];
    notes.forEach(n => {
      // Puce custom emerald
      this.filledRect(PAGE.margin, this.yPos - 2.2, 1.5, 1.5, COLORS.primary);
      this.text(n, PAGE.margin + 5, this.yPos, {
        size: 9, color: COLORS.body, maxWidth: PAGE.width - 2 * PAGE.margin - 5,
      });
      this.yPos += this._textLineHeight(n, PAGE.width - 2 * PAGE.margin - 5, 9) + 1.5;
    });
  }

  // ─── PAGE 5 — COURBE DE CHARGE ──────────────────────────────────────────
  buildLoadCurve(ctx) {
    this.doc.addPage();
    this.yPos = PAGE.margin;
    this.sectionHeader('Courbe de charge annuelle', 'Profil de consommation');

    this.intro(
      `Puissance instantanée appelée par le site sur ${ctx.year}, mesurée par les compteurs ENEDIS. Les paliers horizontaux matérialisent la PS actuelle (rouge) et la PS retenue (verte) : tout pic franchissant ces lignes déclencherait un dépassement facturable.`,
    );

    const chartW = PAGE.width - 2 * PAGE.margin;
    const chartH = 95;

    const points = (ctx.loadCurveSample || []).map(p => ({
      t: new Date(p.timestamp).getTime(),
      v: Number(p.puissance_active_kw) || 0,
    })).sort((a, b) => a.t - b.t);

    if (points.length > 1) {
      const psActuelKw = ctx.psActualMax || 0;
      const psRetenuKw = ctx.psProposedMax || 0;

      // Format ISO court pour labels (Chart.js sans adapter date-fns → on convertit en strings)
      const labels = points.map(p => new Date(p.t));
      const data = points.map(p => p.v);

      // Annotations horizontales : PS actuelle (rouge dashed) + PS retenue (verte dashed)
      const annotations = {
        psActuelle: {
          type: 'line',
          yMin: psActuelKw, yMax: psActuelKw,
          borderColor: COLORS.red,
          borderWidth: 3,
          borderDash: [8, 6],
          label: {
            display: true,
            content: `PS actuelle · ${psActuelKw} kVA`,
            position: 'end',
            backgroundColor: COLORS.red,
            color: COLORS.white,
            font: { size: 18, weight: 'bold' },
            padding: 6,
          },
        },
      };
      if (psRetenuKw > 0 && psRetenuKw !== psActuelKw) {
        annotations.psRetenue = {
          type: 'line',
          yMin: psRetenuKw, yMax: psRetenuKw,
          borderColor: COLORS.primary,
          borderWidth: 3,
          borderDash: [8, 6],
          label: {
            display: true,
            content: `PS retenue · ${psRetenuKw} kVA`,
            position: 'start',
            backgroundColor: COLORS.primary,
            color: COLORS.white,
            font: { size: 18, weight: 'bold' },
            padding: 6,
          },
        };
      }

      // Étiquettes X = noms de mois (sans adapter date-fns) — on prend l'index
      // et on génère un libellé court mois/jour si plage > 60j sinon HH:MM.
      const tMin = points[0].t;
      const tMax = points[points.length - 1].t;
      const spanDays = (tMax - tMin) / 86400000;
      const xLabels = labels.map(d => {
        if (spanDays > 60) return d.toLocaleDateString('fr-FR', { month: 'short' });
        if (spanDays > 2) return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      });

      const lineConfig = {
        type: 'line',
        data: {
          labels: xLabels,
          datasets: [{
            label: 'Puissance active (kW)',
            data,
            borderColor: COLORS.secondary,
            backgroundColor: 'rgba(30, 64, 175, 0.10)',
            borderWidth: 1.5,
            pointRadius: 0,
            tension: 0.15,
            fill: true,
          }],
        },
        options: {
          plugins: {
            legend: { display: true, position: 'top', labels: { font: { size: 22 }, color: COLORS.gray700, boxWidth: 18 } },
            title: { display: true, text: `Courbe de charge ${ctx.year}`, font: { size: 24, weight: 'bold' }, color: COLORS.gray900, padding: { top: 4, bottom: 12 } },
            tooltip: { enabled: false },
            annotation: { annotations },
          },
          scales: {
            x: {
              ticks: {
                font: { size: 18 }, color: COLORS.gray500,
                maxTicksLimit: 12, autoSkip: true, maxRotation: 0,
              },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              ticks: { font: { size: 18 }, color: COLORS.gray500, callback: (v) => `${v} kW` },
              grid: { color: COLORS.gray100 },
              title: { display: true, text: 'kW', color: COLORS.gray500, font: { size: 18 } },
            },
          },
        },
      };
      this._embedChart(lineConfig, chartW, chartH);
    } else {
      // Fallback : placeholder visuel
      this.filledRect(PAGE.margin, this.yPos, chartW, chartH, COLORS.gray50);
      this.borderRect(PAGE.margin, this.yPos, chartW, chartH, COLORS.gray300);
      this.text('Courbe non disponible — relancer l\'analyse pour générer un échantillon.', PAGE.width / 2, this.yPos + chartH / 2, { size: 9, color: COLORS.gray500, align: 'center' });
      this.yPos += chartH + 4;
    }

    // ─── Stats clés en mini-KPI grille 4 colonnes ────────────────────
    this.yPos += 2;
    this.eyebrow('Indicateurs clés', PAGE.margin, this.yPos, COLORS.muted);
    this.yPos += 5;

    const stats = [
      { label: 'Consommation annuelle', value: `${fmtNum((ctx.energieAnnuelleKwh || 0) / 1000)} MWh` },
      { label: 'Puissance instant. max', value: `${fmtNum(ctx.puissanceMaxKw)} kW` },
      { label: 'Points analysés', value: fmtNum(ctx.nbPointsAnalyses) },
      { label: 'Dépassements', value: fmtNum(ctx.nbDepassements) },
    ];
    const sW = (PAGE.width - 2 * PAGE.margin - 3 * 4) / 4;
    const sH = 22;
    stats.forEach((s, i) => {
      const x = PAGE.margin + i * (sW + 4);
      this.filledRect(x, this.yPos, sW, sH, COLORS.surface);
      // Barre latérale signature emerald
      this.filledRect(x, this.yPos, 2, sH, COLORS.primary);
      this.text(s.label.toUpperCase(), x + 5, this.yPos + 7, {
        size: 6.5, color: COLORS.muted, bold: true, tracking: 0.3,
      });
      this.text(s.value, x + 5, this.yPos + 16, {
        size: 12, color: COLORS.ink, bold: true,
      });
    });
    this.yPos += sH + 4;
  }

  // ─── PAGE 6 — VERSION D'UTILISATION ────────────────────────────────────
  buildVersionUtilisation(ctx) {
    if (!ctx.versionDetails) return;
    this.doc.addPage();
    this.yPos = PAGE.margin;
    this.sectionHeader("Version d'utilisation", 'Recommandation tarifaire');

    // Le résultat sauvegardé en BDD peut avoir 2 formes :
    //   - format "plat" issu de adaptedResult ({cout_cu, cout_mu, cout_lu, couts_par_version, …})
    //   - format brut moteur Python (financial_analysis.cout_annuel_cu / _mu / _lu)
    const vd = ctx.versionDetails || {};
    const costs = ctx.versionCosts
      || vd.couts_par_version
      || vd.financial_analysis?.couts_par_version
      || {
          CU: vd.cout_cu ?? vd.financial_analysis?.cout_annuel_cu ?? null,
          MU: vd.cout_mu ?? vd.financial_analysis?.cout_annuel_mu ?? null,
          LU: vd.cout_lu ?? vd.financial_analysis?.cout_annuel_lu ?? null,
        };
    const versions = ['CU', 'MU', 'LU'].filter(k => costs[k] != null);
    const actuelle = ctx.versionActuelle || vd.current_version || null;
    const recommended = ctx.versionRecommended
      || vd.recommended_version
      || (versions.length ? versions.reduce((a, b) => costs[a] <= costs[b] ? a : b) : null);
    const chosen = ctx.versionChosen || recommended;

    const labels = {
      CU: { label: 'Courte Utilisation', sub: 'Profil consommation modérée' },
      MU: { label: 'Moyenne Utilisation', sub: 'Profil consommation intermédiaire' },
      LU: { label: 'Longue Utilisation', sub: 'Profil consommation soutenue' },
    };

    this.intro(
      `Chaque contrat ENEDIS expose plusieurs grilles tarifaires de version d'utilisation (Courte CU, Moyenne MU, Longue LU). Les abonnements et les tarifs variables varient en sens inverse : la CU a l'abonnement le plus faible et l'énergie la plus chère, la LU l'inverse. Le bon choix dépend du taux d'utilisation du site (MWh annuels / PS max × 8760 h).`,
    );

    // ─── Cards par version (2 ou 3 selon les versions disponibles) ────
    const nbCards = versions.length;
    if (nbCards > 0) {
      const cardW = (PAGE.width - 2 * PAGE.margin - 6 * (nbCards - 1)) / nbCards;
      const cardH = 44;
      versions.forEach((key, i) => {
        const isChosen = key === chosen;
        const isRecommended = key === recommended;
        const isActuel = key === actuelle;
        const x = PAGE.margin + i * (cardW + 6);
        const cardColor = isChosen ? COLORS.primary : COLORS.muted;

        this.filledRect(x + 0.3, this.yPos + 0.3, cardW, cardH, COLORS.lineSoft);
        this.filledRect(x, this.yPos, cardW, cardH, isChosen ? COLORS.primarySoft : COLORS.white);
        this.borderRect(x, this.yPos, cardW, cardH,
          isChosen ? COLORS.primary : COLORS.line, isChosen ? 0.5 : 0.2);

        if (isChosen) {
          const badgeW = 22, badgeH = 6;
          this.filledRect(x + cardW - badgeW - 4, this.yPos + 4, badgeW, badgeH, COLORS.primary);
          this.text('RETENU', x + cardW - badgeW / 2 - 4, this.yPos + 8.2, {
            size: 6.5, color: COLORS.white, bold: true, align: 'center', tracking: 0.6,
          });
        } else if (isRecommended) {
          const badgeW = 28, badgeH = 6;
          this.filledRect(x + cardW - badgeW - 4, this.yPos + 4, badgeW, badgeH, COLORS.accent);
          this.text('RECOMMANDÉ', x + cardW - badgeW / 2 - 4, this.yPos + 8.2, {
            size: 6.5, color: COLORS.white, bold: true, align: 'center', tracking: 0.6,
          });
        } else if (isActuel) {
          const badgeW = 18, badgeH = 6;
          this.filledRect(x + cardW - badgeW - 4, this.yPos + 4, badgeW, badgeH, COLORS.muted);
          this.text('ACTUEL', x + cardW - badgeW / 2 - 4, this.yPos + 8.2, {
            size: 6.5, color: COLORS.white, bold: true, align: 'center', tracking: 0.6,
          });
        }

        this.eyebrow(key, x + 5, this.yPos + 10, cardColor);
        this.text(labels[key].label, x + 5, this.yPos + 18, {
          size: 11, color: COLORS.ink, bold: true,
        });
        this.text(labels[key].sub, x + 5, this.yPos + 23, {
          size: 7.5, color: COLORS.muted,
        });
        this.text(fmtEUR(costs[key], 0), x + 5, this.yPos + 37, {
          size: 16, color: cardColor, bold: true,
        });
        this.doc.setFontSize(16);
        this.doc.setFont('helvetica', 'bold');
        const valW = this.doc.getTextWidth(fmtEUR(costs[key], 0));
        this.text('/an', x + 5 + valW + 1.5, this.yPos + 37, {
          size: 8.5, color: COLORS.muted,
        });
      });
      this.yPos += cardH + 10;
    }

    // ─── Bar chart horizontal : 1 barre par version ──────────────────
    if (versions.length > 0) {
      const versionConfig = {
        type: 'bar',
        data: {
          labels: versions.map(k => `${labels[k].label} (${k})`),
          datasets: [{
            label: 'Coût TURPE acheminement / an',
            data: versions.map(k => Number(costs[k]) || 0),
            backgroundColor: versions.map(k => k === recommended ? COLORS.primary : COLORS.gray500),
            borderRadius: 6,
            maxBarThickness: 38,
          }],
        },
        options: {
          indexAxis: 'y',
          plugins: {
            legend: { display: false },
            title: { display: true, text: "Coût annuel d'acheminement par version d'utilisation", font: { size: 24, weight: 'bold' }, color: COLORS.gray900, padding: { top: 4, bottom: 12 } },
            tooltip: { enabled: false },
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: { font: { size: 18 }, color: COLORS.gray500, callback: (v) => `${(v / 1000).toFixed(1)}k €` },
              grid: { color: COLORS.gray100 },
            },
            y: {
              ticks: { font: { size: 22, weight: 'bold' }, color: COLORS.gray700 },
              grid: { display: false },
            },
          },
        },
      };
      const chartW = PAGE.width - 2 * PAGE.margin;
      this._embedChart(versionConfig, chartW, 55);
    }

    // ─── Callout : impact du passage de la version actuelle vers la version retenue ──
    if (actuelle && chosen && actuelle !== chosen && costs[actuelle] != null && costs[chosen] != null) {
      const impactValue = costs[actuelle] - costs[chosen]; // positif = gain
      const impactColor = impactValue > 0 ? COLORS.primary : COLORS.red;
      const impactBg = impactValue > 0 ? COLORS.primarySoft : COLORS.redLight;
      const label = impactValue > 0 ? 'économie' : 'surcoût';
      const diffCalloutH = 16;
      this.filledRect(PAGE.margin, this.yPos, PAGE.width - 2 * PAGE.margin, diffCalloutH, impactBg);
      this.filledRect(PAGE.margin, this.yPos, 3, diffCalloutH, impactColor);
      this.text(
        `Passage ${actuelle} → ${chosen} : ${label} de ${fmtEUR(Math.abs(impactValue))}/an pour le client.`,
        PAGE.margin + 8, this.yPos + 10, { size: 10, color: impactColor, bold: true },
      );
      this.yPos += diffCalloutH + 8;
    } else if (actuelle && chosen === actuelle) {
      // Cas "déjà optimal"
      const diffCalloutH = 16;
      this.filledRect(PAGE.margin, this.yPos, PAGE.width - 2 * PAGE.margin, diffCalloutH, COLORS.primarySoft);
      this.filledRect(PAGE.margin, this.yPos, 3, diffCalloutH, COLORS.primary);
      this.text(
        `Votre contrat est déjà sur la version optimale (${actuelle}). Aucun changement à mener.`,
        PAGE.margin + 8, this.yPos + 10, { size: 10, color: COLORS.primaryDeep, bold: true },
      );
      this.yPos += diffCalloutH + 8;
    }

    // Explication
    this.sectionHeader('Méthode de calcul', 'Détail méthodologique');
    const tauxTxt = ctx.tauxUtilisation != null
      ? `${ctx.tauxUtilisation.toFixed(1)} %`
      : 'non disponible';
    const explain = recommended === 'CU'
      ? `Pour ce site, le taux d'utilisation calculé (${tauxTxt}) reste sous le seuil de bascule. L'abonnement LU plus élevé ne serait pas amorti par la baisse du tarif variable. La CU minimise donc le coût total d'acheminement.`
      : `Pour ce site, le taux d'utilisation calculé (${tauxTxt}) dépasse le seuil de bascule. Le surcoût d'abonnement LU est plus que compensé par la baisse du tarif variable appliqué aux MWh consommés. La LU minimise donc le coût total d'acheminement.`;
    this.text(explain, PAGE.margin, this.yPos, {
      size: 9.5, color: COLORS.body, maxWidth: PAGE.width - 2 * PAGE.margin,
    });
    this.yPos += this._textLineHeight(explain, PAGE.width - 2 * PAGE.margin, 9.5) + 4;

    // Mise en garde — petit encart italique discret
    this.filledRect(PAGE.margin, this.yPos - 2, 1, 12, COLORS.muted);
    this.text(
      `Mise en garde : ce verdict est valable au profil de consommation actuel. Une évolution significative d'activité (extension, fermeture, électrification de procédés) peut basculer l'arbitrage. À réévaluer chaque exercice.`,
      PAGE.margin + 4, this.yPos + 2, {
        size: 8, color: COLORS.muted, italic: true,
        maxWidth: PAGE.width - 2 * PAGE.margin - 4,
      },
    );
  }

  // ─── FOOTER GLOBAL ─────────────────────────────────────────────────────
  /**
   * Footer discret : filet fin + 3 zones (marque / doc ID / pagination).
   * Skip cover. Pagination en format "02 / 06" (padding zero, plus pro).
   */
  addFooters() {
    const total = this.doc.internal.getNumberOfPages();
    for (let i = 2; i <= total; i++) {
      this.doc.setPage(i);
      // Filet top footer (très clair, presque invisible)
      this.doc.setDrawColor(COLORS.line);
      this.doc.setLineWidth(0.15);
      this.doc.line(PAGE.margin, PAGE.height - 12, PAGE.width - PAGE.margin, PAGE.height - 12);

      // Marque (gauche) — wordmark tracked + label sobre
      this.text('ENERGYGROUP 360', PAGE.margin, PAGE.height - 7, {
        size: 7, color: COLORS.muted, bold: true, tracking: 0.5,
      });
      this.text('Rapport TURPE', PAGE.margin + 36, PAGE.height - 7, {
        size: 7, color: COLORS.muted,
      });

      // Doc ID (centre) — pour traçabilité documentaire
      this.text(this.docIdValue || '—', PAGE.width / 2, PAGE.height - 7, {
        size: 7, color: COLORS.muted, align: 'center',
      });

      // Pagination (droite, format "02 / 06" — padding zero)
      const pageStr = `${String(i).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      this.text(pageStr, PAGE.width - PAGE.margin, PAGE.height - 7, {
        size: 7, color: COLORS.ink, bold: true, align: 'right',
      });
    }
  }

  // ─── Construction du contexte de génération ────────────────────────────
  _buildContext(analysisData) {
    const t6 = analysisData?.analysisResults?.turpe6_details || {};
    const t7 = analysisData?.analysisResults?.turpe7_details || {};
    const summary = analysisData?.analysisSummary || {};
    const pdlInfo = analysisData?.pdl || {};
    const enedisInfos = Array.isArray(pdlInfo.enedis_infos) ? pdlInfo.enedis_infos[0] : pdlInfo.enedis_infos;
    const optionTarifaire = enedisInfos?.option_tarifaire_turpe || t6.details_calcul?.analytics?.tension_domain || '—';
    const year = analysisData?.year || new Date().getFullYear() - 1;

    const csFixeParams = t6.composantes_detail?.CS_FIXE?.parametres || {};
    const psCurrent = {
      hph: csFixeParams.ps_hph_kw || 0,
      hch: csFixeParams.ps_hch_kw || 0,
      hpb: csFixeParams.ps_hpb_kw || 0,
      hcb: csFixeParams.ps_hcb_kw || 0,
    };

    // Choix final retenu : prioriser le choix explicite du user (chosen_optimization),
    // sinon prendre la stratégie qui a généré la plus grosse économie réelle (manual
    // ou auto), sinon fallback sur celle qui existe.
    const autoSaving = Number(summary.best_saving_eur) || 0;
    const manualSaving = Number(summary.manual_saving_eur) || 0;
    const hasAuto = autoSaving > 0 && summary.best_saving_strategy_json;
    const hasManual = manualSaving > 0 && summary.manual_strategy_json;
    let chosenStrategy = summary.chosen_optimization;
    if (!chosenStrategy) {
      if (hasManual && manualSaving >= autoSaving) chosenStrategy = 'manual';
      else if (hasAuto) chosenStrategy = 'auto';
      else if (hasManual) chosenStrategy = 'manual';
      else chosenStrategy = 'auto';
    }
    const stratObj = chosenStrategy === 'manual'
      ? summary.manual_strategy_json
      : summary.best_saving_strategy_json;

    // Puissances proposées selon stratégie retenue. Fallback : si stratObj n'a
    // pas de power_subscriptions, on garde les PS actuelles.
    let psProposed = { ...psCurrent };
    if (chosenStrategy === 'auto' && stratObj?.new_power_subscriptions) {
      const np = stratObj.new_power_subscriptions;
      psProposed = {
        hph: Number(np.hph) || psCurrent.hph,
        hch: Number(np.hch) || psCurrent.hch,
        hpb: Number(np.hpb) || psCurrent.hpb,
        hcb: Number(np.hcb) || psCurrent.hcb,
      };
    } else if (chosenStrategy === 'manual') {
      // Le manual_strategy_json contient impact_analysis.new_powers (selon le format
      // moteur Python /api/v4/optimize-manual) — sinon power_adjustments[i].new_value.
      const newPowers = stratObj?.impact_analysis?.new_powers || stratObj?.new_power_subscriptions;
      if (newPowers) {
        psProposed = {
          hph: Number(newPowers.hph) || psCurrent.hph,
          hch: Number(newPowers.hch) || psCurrent.hch,
          hpb: Number(newPowers.hpb) || psCurrent.hpb,
          hcb: Number(newPowers.hcb) || psCurrent.hcb,
        };
      } else if (Array.isArray(stratObj?.power_adjustments)) {
        // Le payload Python /api/v4/optimize-manual nomme le champ `proposed_value`
        // (pas `new_value`). On accepte les 2 noms par compat. Les valeurs sont
        // sérialisées en string ('60') donc Number() obligatoire.
        stratObj.power_adjustments.forEach(adj => {
          const k = (adj.period || '').toLowerCase();
          const v = Number(adj.proposed_value ?? adj.new_value);
          if (psProposed[k] != null && v > 0) psProposed[k] = v;
        });
      }
    }

    let psSaving = chosenStrategy === 'manual'
      ? (Number(stratObj?.impact_analysis?.annual_savings) || manualSaving || 0)
      : (Number(stratObj?.annual_saving_eur) || autoSaving || 0);

    const analytics = t6.details_calcul?.analytics || {};
    const psMaxAtteint = {
      hph: Number(analytics.puissance_max_hph_kw) || 0,
      hch: Number(analytics.puissance_max_hch_kw) || 0,
      hpb: Number(analytics.puissance_max_hpb_kw) || 0,
      hcb: Number(analytics.puissance_max_hcb_kw) || 0,
    };
    const psActualMax = Math.max(psCurrent.hph, psCurrent.hch, psCurrent.hpb, psCurrent.hcb);
    const psProposedMax = Math.max(psProposed.hph, psProposed.hch, psProposed.hpb, psProposed.hcb);

    const turpe6Ttc = Number(t6.cout_total_annuel) || (Number(t6.cout_total_htva || 0) * 1.2);
    const turpe7Ttc = Number(t7.cout_total_annuel) || (Number(t7.cout_total_htva || 0) * 1.2);

    const versionDetails = summary.version_of_use_details_json || null;
    const versionChosen = summary.chosen_version_of_use || summary.recommended_version_of_use;
    const versionRecommended = summary.recommended_version_of_use;
    // Source de vérité prioritaire : version réelle calculée par le moteur
    // Python (current_version dans le payload version_of_use). Fallback :
    // parsing du libellé option_tarifaire (peu fiable, ne distingue pas MU).
    const versionActuelle = versionDetails?.current_version
      || ((optionTarifaire || '').includes('CU') ? 'CU' : (optionTarifaire || '').includes('LU') ? 'LU' : null);
    const versionChange = versionChosen && versionActuelle && versionChosen !== versionActuelle;

    // Coûts par version (CU/MU/LU) tels que retournés par le moteur Python
    const versionCosts = versionDetails?.couts_par_version
      || versionDetails?.financial_analysis?.couts_par_version
      || null;

    // Économie liée au changement de version d'utilisation (si chosen != actuelle).
    // Convention : positif = gain pour le client.
    let vouSaving = 0;
    if (!summary.resume && versionChange && versionCosts && versionActuelle && versionChosen) {
      const cActuel = Number(versionCosts[versionActuelle]) || 0;
      const cChoisi = Number(versionCosts[versionChosen]) || 0;
      vouSaving = cActuel - cChoisi;
    }

    // Résumé PARTAGÉ (24/10/2026) : seule source des montants quand il est présent —
    // version = coût(actuelle) − coût(retenue), non mesurée si la version actuelle n'est pas
    // confirmée, négative jamais présentée comme économie. Le PDF n'est produit que pour une
    // analyse concluante (PDFExporter + POST /api/turpe/reports).
    const resume = summary.resume || null;
    if (resume) {
      psSaving = Number(resume.ps?.economie_eur) || 0;
      vouSaving = Number(resume.fta?.economie_eur) || 0;
    }
    const vouStatut = resume?.fta?.statut || null;
    const vouCause = resume?.fta?.cause || null;
    const vouPuissanceCalcul = resume?.fta?.puissance_calcul || null;

    const coutActuel = turpe6Ttc;
    // Le coût optimisé inclut PS + changement de version d'utilisation
    const coutOptimise = coutActuel - psSaving - vouSaving;
    const totalSaving = resume ? (Number(resume.economie_totale_eur) || 0) : psSaving + vouSaving;

    const choiceLabel = `Stratégie PS : ${chosenStrategy === 'manual' ? 'Optimisation manuelle' : 'Optimisation IA'} · Version : ${versionChosen || versionActuelle || '—'}`;
    const choiceLabelShort = chosenStrategy === 'manual' ? 'choix manuel' : 'reco IA';

    const energieAnnuelleKwh =
      (Number(analytics.energie_hph_kwh) || 0) +
      (Number(analytics.energie_hch_kwh) || 0) +
      (Number(analytics.energie_hpb_kwh) || 0) +
      (Number(analytics.energie_hcb_kwh) || 0);

    const tauxUtilisation = psActualMax > 0 && energieAnnuelleKwh > 0
      ? (energieAnnuelleKwh / (psActualMax * 8760)) * 100
      : null;

    return {
      pdlInfo,
      year,
      optionTarifaire,
      psCurrent,
      psCurrentLabel: `HPH ${psCurrent.hph} - HCH ${psCurrent.hch} - HPB ${psCurrent.hpb} - HCB ${psCurrent.hcb} kVA`,
      psProposed,
      psMaxAtteint,
      psActualMax,
      psProposedMax,
      psSaving,
      vouSaving,
      vouStatut, vouCause, vouPuissanceCalcul,
      versionCosts,
      psSafetyMargin: null,
      t6, t7,
      turpe6Ttc, turpe7Ttc,
      coutActuel, coutOptimise, totalSaving,
      nbDepassements: Number(analytics.nb_depassements) || 0,
      puissanceMaxKw: Number(analytics.puissance_max_kw) || 0,
      nbPointsAnalyses: Number(analytics.nb_points_analyses) || 0,
      energieAnnuelleKwh,
      tauxUtilisation,
      loadCurveSample: analytics.load_curve_sample || [],
      choiceLabel, choiceLabelShort,
      versionDetails, versionChosen, versionRecommended, versionActuelle, versionChange,
      periodLabel: year ? `01/01/${year}  -  31/12/${year}` : '-',
      logoDataUrl: analysisData?.logoDataUrl || null,
    };
  }

  // ─── Utilitaires ────────────────────────────────────────────────────────
  _formatAddress(addr) {
    if (!addr) return '—';
    if (typeof addr === 'string') return addr;
    return [addr.street, addr.postal_code, addr.city].filter(Boolean).join(' · ') || '—';
  }
  _textLineHeight(text, maxWidth, fontSize) {
    if (!text) return 0;
    this.doc.setFontSize(fontSize);
    const lines = this.doc.splitTextToSize(text, maxWidth);
    return lines.length * (fontSize * 0.45) + 2;
  }

  // ─── Public API ─────────────────────────────────────────────────────────
  generatePDF(analysisData) {
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.doc.setFont('helvetica', 'normal');
    this.yPos = PAGE.margin;
    // Doc ID partagé entre cover (RÉF.) et footer pages
    this.docIdValue = buildDocId();

    const ctx = this._buildContext(analysisData);

    this.buildCover(ctx);
    this.buildSynthesis(ctx);
    this.buildComparatif(ctx);
    this.buildPSOptim(ctx);
    this.buildLoadCurve(ctx);
    this.buildVersionUtilisation(ctx);
    this.addFooters();

    return this.doc;
  }

  downloadPDF(analysisData, filename) {
    this.generatePDF(analysisData).save(filename);
  }

  // Retourne le PDF en base64 (sans le préfixe data:application/pdf;base64,)
  // utilisé par PDFExporter pour POST /api/turpe/reports (persistance).
  getPDFBase64(analysisData) {
    const doc = this.generatePDF(analysisData);
    const dataUri = doc.output('datauristring');
    const idx = dataUri.indexOf(',');
    return idx >= 0 ? dataUri.substring(idx + 1) : dataUri;
  }
}

export default new TurpePDFService();
